import React, { memo, useState } from 'react';
import PropTypes from 'prop-types';
import { ErrorBoundary, LoadingSpinner, SectionContainer, Button } from '../common';
import { WeatherDisplay } from '../weather';

const Schedule = ({ 
  schedule,
  weather,
  tides,
  waterTemp,
  moonData,
  flagStatus,
  loadingWeather,
  weatherError,
  onFetchWeather,
  onUpdateFlag,
  onTestApi,
  onClearCache
}) => {
  const [windyLoading, setWindyLoading] = useState(true);
  return (
    <SectionContainer>
      <h2 className="text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-orange-600 dark:from-blue-400 dark:to-orange-400 animate-fade-in">📅 Daily Schedule</h2>
      
      {/* Enhanced Current Weather - More Prominent */}
      <div className="bg-gradient-to-br from-blue-500 to-orange-500 rounded-xl p-1 shadow-xl animate-on-scroll animate-gradient">
        <div className="bg-white dark:bg-gray-800 bg-opacity-95 dark:bg-opacity-95 backdrop-blur rounded-lg p-4">
          <h3 className="text-lg font-bold text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-orange-600 dark:from-blue-400 dark:to-orange-400 mb-3 text-center">🌤️ Cape San Blas Weather</h3>
          <ErrorBoundary>
            {loadingWeather ? (
              <LoadingSpinner message="Loading weather data..." />
            ) : weatherError ? (
              <div className="text-center py-4">
                <p className="text-sm text-red-600 dark:text-red-400 mb-2">Weather data unavailable</p>
                <p className="text-xs text-gray-600 dark:text-gray-400 mb-3">{weatherError}</p>
                <Button onClick={onFetchWeather} variant="primary">
                  Try again
                </Button>
                <div className="mt-3 space-y-1">
                  <Button
                    onClick={onTestApi}
                    variant="link"
                    size="small"
                    className="block w-full text-xs"
                  >
                    Test API Connection
                  </Button>
                  <Button
                    onClick={onClearCache}
                    variant="link"
                    size="small"
                    className="block w-full text-xs text-red-600 dark:text-red-400"
                  >
                    Clear Cache & Retry
                  </Button>
                </div>
              </div>
            ) : (
              <WeatherDisplay
                weather={weather}
                tides={tides}
                waterTemp={waterTemp}
                moonData={moonData}
                flagStatus={flagStatus}
                onRefresh={onFetchWeather}
                onUpdateFlag={onUpdateFlag}
              />
            )}
          </ErrorBoundary>
        </div>
      </div>
      
      {/* Windy Weather Map - Compact View */}
      <div className="bg-white/95 dark:bg-gray-800/95 backdrop-blur rounded-xl shadow-lg p-5 border border-orange-100 dark:border-gray-700 animate-on-scroll card-hover">
        <h3 className="font-semibold text-lg text-gray-800 dark:text-gray-100 mb-3">🌊 Live Weather Radar</h3>
        <div className="relative rounded-lg overflow-hidden" style={{ height: '300px' }}>
          {windyLoading && (
            <div className="absolute inset-0 flex items-center justify-center bg-gray-100 dark:bg-gray-700">
              <div className="text-center">
                <div className="w-8 h-8 animate-spin mx-auto text-orange-500 mb-2">
                  <svg className="w-full h-full" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                </div>
                <p className="text-sm text-gray-600 dark:text-gray-400">Loading radar...</p>
              </div>
            </div>
          )}
          <iframe
            src="https://embed.windy.com/embed2.html?lat=29.678&lon=-85.3674&detailLat=29.678&detailLon=-85.3674&width=650&height=300&zoom=11&level=surface&overlay=wind&product=ecmwf&menu=&message=true&marker=true&calendar=now&pressure=&type=map&location=coordinates&detail=&metricWind=mph&metricTemp=%C2%B0F&radarRange=-1"
            frameBorder="0"
            style={{ width: '100%', height: '100%' }}
            title="Windy Weather Map"
            sandbox="allow-scripts allow-same-origin"
            loading="lazy"
            onLoad={() => setWindyLoading(false)}
          ></iframe>
        </div>
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-2 text-center">
          Live wind & weather conditions • Tap for full screen
        </p>
      </div>
      
      {/* Daily Schedule Cards */}
      {schedule.map((day, idx) => (
        <div key={idx} className="bg-white/95 dark:bg-gray-800/95 backdrop-blur rounded-xl shadow-lg p-5 border border-orange-100 dark:border-gray-700 card-hover animate-on-scroll touch-scale">
          <h3 className="font-semibold text-lg text-transparent bg-clip-text bg-gradient-to-r from-orange-600 to-pink-600 dark:from-orange-400 dark:to-pink-400 mb-3">{day.date}</h3>
          <ul className="space-y-2">
            {day.events.map((event, eidx) => (
              <li key={eidx} className="text-gray-700 dark:text-gray-300 text-sm flex items-start">
                <span className="text-orange-400 dark:text-orange-300 mr-2">•</span>
                <span>{event}</span>
              </li>
            ))}
          </ul>
        </div>
      ))}
    </SectionContainer>
  );
};

Schedule.propTypes = {
  schedule: PropTypes.arrayOf(PropTypes.shape({
    date: PropTypes.string.isRequired,
    events: PropTypes.arrayOf(PropTypes.string).isRequired
  })).isRequired,
  weather: PropTypes.object,
  tides: PropTypes.array,
  waterTemp: PropTypes.number,
  moonData: PropTypes.object,
  flagStatus: PropTypes.object,
  loadingWeather: PropTypes.bool,
  weatherError: PropTypes.string,
  onFetchWeather: PropTypes.func.isRequired,
  onUpdateFlag: PropTypes.func.isRequired,
  onTestApi: PropTypes.func,
  onClearCache: PropTypes.func
};

export default memo(Schedule);